import {
  AfterContentChecked,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  Renderer2,
} from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { TranslateService } from "@ngx-translate/core";
import { forkJoin } from "rxjs";
import {
  DesnitrificacionViewModel,
  DigestionAnaerobiaFangoViewModel,
  PrimaIncluidaViewModel,
  RecuperacionFosforoViewModel,
  TipoUsoAguaViewModel,
} from "src/app/_core/models/centros/centro-list-base.model";
import { CentrosViewModel } from "src/app/_core/models/centros/centro.model";
import { DecimalService } from "src/app/_core/providers/decimals.service";
import { MasterService } from "src/app/_core/providers/master.service";
import { CentroPageComponent } from "../../../pages/centro/centro.page";

@Component({
    selector: "app-centro-operation-data-card",
    templateUrl: "./centro-operation-data-card.component.html",
    styleUrls: ["./centro-operation-data-card.component.scss"],
    standalone: false
})
export class CentroOperationDataCardComponent
  implements OnInit, AfterContentChecked
{
  @Input() centro!: CentrosViewModel;
  @Input() parent: CentroPageComponent;
  @Output() operationFormComplete = new EventEmitter<boolean>();
  @Output() centroOperationData = new EventEmitter<FormGroup>();

  public primaIncluidaList: PrimaIncluidaViewModel[] = [];
  public desnitrificacionList: DesnitrificacionViewModel[] = [];
  public digestionAnaerobiaFangoList: DigestionAnaerobiaFangoViewModel[] = [];
  public recuperacionFosforoList: RecuperacionFosforoViewModel[] = [];
  public usoAguaProductoList: TipoUsoAguaViewModel[] = [];
  public usoAguaRegeneradaList: TipoUsoAguaViewModel[] = [];

  myOperationForm: FormGroup;
  countControls: number = 0;
  countFilled: number = 0;

  constructor(
    private readonly _masterService: MasterService,
    private readonly _translateService: TranslateService,
    public renderer: Renderer2,
    public el: ElementRef,
    private readonly fb: FormBuilder,
    public decimalService: DecimalService
  ) {}

  // Etiquetas de opción para los app-ds-select (maestros con clave i18n).
  energyBonusLabelOf = (item: PrimaIncluidaViewModel): string =>
    this._translateService.instant("centro.energyBonuses." + item.codigo);
  tipoAguaLabelOf = (item: TipoUsoAguaViewModel): string =>
    this._translateService.instant("centro.tipoAgua." + item.codigo);
  desnitrificacionLabelOf = (item: DesnitrificacionViewModel): string =>
    this._translateService.instant("centro.desnitrifications." + item.codigo);
  digestionLabelOf = (item: DigestionAnaerobiaFangoViewModel): string =>
    this._translateService.instant("centro.anaerobicDigestions." + item.codigo);
  recupFosforoLabelOf = (item: RecuperacionFosforoViewModel): string =>
    this._translateService.instant("centro.retrieveMatches." + item.codigo);

  // Modo lectura / edición (gobernado por la página).
  get editing(): boolean {
    return !!(this.parent?.isEditing || this.parent?.isCreating);
  }
  // Grupos temáticos (claves del modelo de visibilidad de la página).
  readonly G_ENERGIA = [
    "consumoEnergeticoAnual",
    "consumoEnergeticoAutoproducido",
    "bonifPenalEnergetica",
  ];
  readonly G_AGUA_PRODUCTO = [
    "usoPriAguaProd",
    "usoPriPorc",
    "usoSecAguaProd",
    "usoSecPorc",
  ];
  readonly G_AGUA_REGENERADA = ["usoPriAguaRege", "usoPriPorcRege"];
  readonly G_VOLUMENES = [
    "prodAgua",
    "volAguaDist",
    "volCaudalRecog",
    "iLI",
    "poblaAbaste",
  ];
  readonly G_PROCESOS = [
    "desnitrificacion",
    "digestAnaerobea",
    "recuperacionFosforo",
  ];

  /** ¿El control tiene valor? (para el estado fantasma del tile). */
  has(control: string): boolean {
    const v = this.myOperationForm?.get(control)?.value;
    return v !== null && v !== undefined && v !== "";
  }

  /** Valor numérico formateado al locale del usuario (o "—"). */
  fmt(control: string): string {
    const v = this.myOperationForm?.get(control)?.value;
    if (v === null || v === undefined || v === "") {
      return "—";
    }
    const n = Number(v);
    return Number.isFinite(n)
      ? new Intl.NumberFormat(this.parent?.getUserLocale() ?? "es-ES", {
          maximumFractionDigits: 2,
        }).format(n)
      : `${v}`;
  }

  // Código de la opción seleccionada de un select (para resolver su etiqueta).
  codeOf(list: { id: number; codigo: string }[], id: unknown): string | null {
    return list?.find((x) => x.id === id)?.codigo ?? null;
  }

  /** Etiqueta traducida de la opción seleccionada de un select (o "—"). */
  selLabel(
    list: { id: number; codigo: string }[],
    control: string,
    i18nPrefix: string,
  ): string {
    const code = this.codeOf(list, this.myOperationForm?.get(control)?.value);
    return code ? this._translateService.instant(i18nPrefix + code) : "—";
  }

  // Events
  async ngOnInit() {
    this._loadData();
    await this.loadCentroOperationDataFormGroup();
  }

  private _loadData(): void {
    forkJoin([
      this._masterService.getPrimaIncluidaList(),
      this._masterService.getDesnitrificacionList(),
      this._masterService.getDigestionAnaerobiaFangoList(),
      this._masterService.getRecuperacionFosforoList(),
      this._masterService.getUsoAgua(true, false),
      this._masterService.getUsoAgua(false, true),
    ]).subscribe(([s1, s2, s3, s4, s5, s6]) => {
      this.primaIncluidaList = s1;
      this.desnitrificacionList = s2;
      this.digestionAnaerobiaFangoList = s3;
      this.recuperacionFosforoList = s4;
      this.usoAguaProductoList = s5;
      this.usoAguaRegeneradaList = s6;
    });
  }

  ngAfterContentChecked(): void {
    this.getFormStatus();
  }

  async loadCentroOperationDataFormGroup() {
    this.myOperationForm = this.fb.group({
      conEnerAn: [this.centro?.conEnerAn],
      conEnerAnAut: [this.centro?.conEnerAnAut],
      primaIncluidaID: [this.centro?.primaIncluidaID],
      desnitrificacionID: [this.centro?.desnitrificacionID],
      digestionAnaerobiaFangoID: [this.centro?.digestionAnaerobiaFangoID],
      recupFosforoID: [this.centro?.recupFosforoID],
      producAgua: [this.centro?.producAgua],
      volumenFacturado: [this.centro?.volumenFacturado],
      voluRecoSan: [this.centro?.voluRecoSan],
      usoPriPorc: [
        this.centro?.usoPriPorc,
        [Validators.max(100), Validators.min(0)],
      ],
      usoPriAguaProdID: [this.centro?.usoSecAguaProdID],
      usoPriPorcRege: [
        this.centro?.usoPriPorcRege,
        [Validators.max(100), Validators.min(0)],
      ],
      usoPriAguaRegeID: [this.centro?.usoPriAguaRegeID],
      usoSecPorc: [
        this.centro?.usoSecPorc,
        [Validators.max(100), Validators.min(0)],
      ],
      usoSecAguaProdID: [this.centro?.usoSecAguaProdID],
      poblaAbaste: [this.centro?.poblaAbaste],
      iLI: [this.centro?.iLI],
      cecoaCentro: [this.centro?.cecoaCentro],
    });

    this.centroOperationData.emit(this.myOperationForm);

    this.myOperationForm.valueChanges.subscribe(() => {
      this.getFormStatus();

      this.centroOperationData.emit(this.myOperationForm);
    });
  }

  getFormStatus() {
    this.countControls = 0;
    this.countFilled = 0;

    Object.keys(this.myOperationForm.controls).forEach((controlName) => {
      const control = this.myOperationForm.get(controlName);

      const controlElement = document.querySelector(
        `[formControlName="${controlName}"]`
      );

      if (controlElement) {
        const hasHiddenClass =
          controlElement.parentElement.classList.contains("hidden");

        if (!hasHiddenClass) {
          this.countControls++;

          if (control?.value !== null && control?.value !== "") {
            this.countFilled++;
          }
        }
      }
    });

    if (this.countFilled !== 0) {
      this.operationFormComplete.emit(this.countControls == this.countFilled);
    }
  }
}
